import React from "react";
import { AppProvider } from "@/state/AppContext";
import { I18nProvider } from "@/i18n";
import { CatalogProvider } from "@/state/CatalogContext";
import { CartProvider } from "@/state/CartContext";
import { RouterProvider, useRouter } from "@/router/Router";
import { HomePage } from "@/pages/HomePage";
import { CatalogPage } from "@/pages/CatalogPage";
import { ProductPage } from "@/pages/ProductPage";
import { CartPage } from "@/pages/CartPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { OrderConfirmationPage } from "@/pages/OrderConfirmationPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { FavoritesPage } from "@/pages/FavoritesPage";
import { OrderHistoryPage } from "@/pages/OrderHistoryPage";
import { OrderDetailPage } from "@/pages/OrderDetailPage";
import { PostsPage } from "@/pages/PostsPage";
import { PostPage } from "@/pages/PostPage";
import { ReviewsPage } from "@/pages/ReviewsPage";
import { SubscriptionReorderPage } from "@/pages/SubscriptionReorderPage";
import { ComparePage } from "@/pages/ComparePage";
import { WishlistPage } from "@/pages/WishlistPage";
import { Splash } from "@/components/Splash";
import { getStartParam } from "@/lib/telegram";
import { LeadPage } from "@/pages/LeadPage";
import { RecipePage, RecipesPage } from "@/pages/RecipesPages";
import { GiftPage } from "@/pages/GiftPage";
import { PassportPage } from "@/pages/PassportPage";
import { useConfig } from "@/lib/useConfig";
import { AddressesPage } from "@/pages/AddressesPage";
import { EventsPage, TicketPage } from "@/pages/EventsPages";
import { FaqPage } from "@/pages/FaqPage";
import { DesktopFrame } from "@/components/desktop/DesktopFrame";
import { TastePage } from "@/pages/TastePage";
import { initAnalytics, track } from "@/lib/analytics";
import type { Route } from "@/router/Router";
import { RouteMeta } from "@/lib/RouteMeta";

function Screens() {
  const { route, navigate } = useRouter();
  // Deep links: t.me/<bot>?startapp=wl_<code> opens a shared wishlist (once per launch).
  React.useEffect(() => {
    initAnalytics();
    track("open");
    // t.me/<bot>?startapp=… deep links: product, article, gift, lead form, wishlist.
    const target = routeFromStartParam(getStartParam());
    if (!target) return;
    try {
      if (sessionStorage.getItem("kms.startHandled")) return;
      sessionStorage.setItem("kms.startHandled", "1");
    } catch {
      /* storage unavailable — still open the link */
    }
    navigate(target);
  }, []);

  switch (route.name) {
    case "home":
      return <HomePage />;
    case "catalog":
      return <CatalogPage />;
    case "product":
      return <ProductPage slug={route.slug} />;
    case "cart":
      return <CartPage />;
    case "checkout":
      return <CheckoutPage />;
    case "orderConfirmation":
      return <OrderConfirmationPage orderId={route.orderId} />;
    case "profile":
      return <ProfilePage />;
    case "favorites":
      return <FavoritesPage />;
    case "orders":
      return <OrderHistoryPage />;
    case "orderDetail":
      return <OrderDetailPage orderId={route.orderId} />;
    case "b2b":
      // The old B2B form is replaced by the lead form (same purpose, more useful data).
      return <LeadPage source="b2b" />;
    case "posts":
      return <PostsPage type={route.type} />;
    case "post":
      return <PostPage slug={route.slug} />;
    case "reviews":
      return <ReviewsPage slug={route.slug} />;
    case "subscription":
      return <SubscriptionReorderPage id={route.id} />;
    case "compare":
      return <ComparePage />;
    case "wishlist":
      return <WishlistPage code={route.code} />;
    case "lead":
      return <LeadPage source={route.source} />;
    case "recipes":
      return <RecipesPage />;
    case "recipe":
      return <RecipePage slug={route.slug} />;
    case "gift":
      return <GiftPage code={route.code} />;
    case "passport":
      return <PassportGate />;
    case "addresses":
      return <AddressesPage />;
    case "events":
      return <EventsPage />;
    case "ticket":
      return <TicketPage id={route.id} />;
    case "faq":
      return <FaqPage category={route.category} />;
    case "taste":
      return <TastePage />;
    case "flavors":
      // The flavour wheel exists on the desktop site only; elsewhere this opens the catalogue.
      return <CatalogPage />;
  }
}

export function App() {
  return (
    <AppProvider>
      <I18nProvider>
        <CatalogProvider>
          <CartProvider>
            <RouterProvider>
              <Splash />
              <RouteMeta />
              <DesktopFrame>
                <div className="app-shell">
                  <Screens />
                </div>
              </DesktopFrame>
            </RouterProvider>
          </CartProvider>
        </CatalogProvider>
      </I18nProvider>
    </AppProvider>
  );
}

/** Maps a ?startapp= payload to a screen. Unknown or referral ("ref_") payloads → null. */
export function routeFromStartParam(param: string | null): Route | null {
  if (!param) return null;
  const [prefix, ...rest] = param.split("_");
  const value = rest.join("_");
  if (!value) return prefix === "lead" ? { name: "lead", source: "link" } : null;
  switch (prefix) {
    case "p":
      return { name: "product", slug: value };
    case "a":
      return { name: "post", slug: value };
    case "gift":
      return { name: "gift", code: value };
    case "lead":
      return { name: "lead", source: value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40) || "link" };
    case "wl":
      return { name: "wishlist", code: value };
    default:
      return null;
  }
}

/** "Кофейный паспорт" only when FEATURE_PASSPORT is on; otherwise fall back to the home screen. */
function PassportGate() {
  const { features } = useConfig();
  return features.passport ? <PassportPage /> : <HomePage />;
}
